import {
  CASE_PLAN_ACCESS_PERMISSIONS,
  CASE_PLAN_CREATE_PERMISSIONS,
  CASE_PLAN_DETAIL_ALL_PLANS_PERMISSIONS,
  CASE_PLAN_MY_PLANS_PERMISSIONS,
  CASE_PLAN_PREVIEW_RELEASE_PERMISSIONS,
  CASE_PLAN_REENTRY_EDIT_DETAIL_PERMISSION,
  REENTRY_PLAN_TYPES,
  REENTRY_VISIBILITY_PLAN_TYPES,
  mapEntitledPlanTypesToApi,
  toVisibilityPlanType,
} from '../constants/reentry-planning.constants';

const LAST_REFRESHED_TIME_OPTIONS = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

export const formatLastRefreshedText = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  return `Last Refreshed ${timestamp.toLocaleTimeString('en-US', LAST_REFRESHED_TIME_OPTIONS)}`;
};

const normalizePermissionName = (permissionName) => permissionName?.toLowerCase() ?? '';

const toPermissionNameSet = (permissionNames) =>
  new Set(permissionNames.map((permissionName) => permissionName.toLowerCase()));

const CASE_PLAN_ACCESS_PERMISSIONS_SET = toPermissionNameSet(CASE_PLAN_ACCESS_PERMISSIONS);

const CASE_PLAN_MY_PLANS_PERMISSIONS_SET = toPermissionNameSet(CASE_PLAN_MY_PLANS_PERMISSIONS);

const CASE_PLAN_PREVIEW_RELEASE_PERMISSIONS_SET = toPermissionNameSet(
  CASE_PLAN_PREVIEW_RELEASE_PERMISSIONS,
);

const CASE_PLAN_CREATE_PERMISSIONS_SET = toPermissionNameSet(CASE_PLAN_CREATE_PERMISSIONS);

const CASE_PLAN_EDIT_PERMISSIONS_SET = toPermissionNameSet([CASE_PLAN_REENTRY_EDIT_DETAIL_PERMISSION]);

const CASE_PLAN_DETAIL_ALL_PLANS_PERMISSIONS_SET = toPermissionNameSet(
  CASE_PLAN_DETAIL_ALL_PLANS_PERMISSIONS,
);

/** True when the granted permission ids include any permission named in `permissionNameSet`. */
const hasAnyGrantedPermission = (permissions, permissionIds, permissionNameSet) =>
  permissions?.some(
    (permission) =>
      permissionNameSet.has(normalizePermissionName(permission.permissionName)) &&
      permissionIds?.includes(permission.permissionId),
  );

export const hasCasePlanAccess = (permissions, permissionIds) =>
  hasAnyGrantedPermission(permissions, permissionIds, CASE_PLAN_ACCESS_PERMISSIONS_SET);

/** True when the staff member holds `case_plan_reentry_view_assigned_detail`. Ignores CBAC-off bypass. */
export const hasCasePlanAssignedDetailAccess = (permissions, permissionIds) =>
  hasAnyGrantedPermission(permissions, permissionIds, CASE_PLAN_MY_PLANS_PERMISSIONS_SET);

/** True when the staff member holds `case_plan_preview_release_access`. Ignores CBAC-off bypass. */
export const hasCasePlanPreviewReleaseAccess = (permissions, permissionIds) =>
  hasAnyGrantedPermission(permissions, permissionIds, CASE_PLAN_PREVIEW_RELEASE_PERMISSIONS_SET);

/**
 * Whether the staff member may open plans they do not edit from All Plans.
 * Uses the same granted-permission mechanism as `hasCasePlanAccess` (not the CBAC
 * `isPermitted` flag path). `case_plan_reentry_view_any_detail` or the preview
 * release fallback grants this.
 */
export const hasCasePlanAllPlansDetailAccess = (permissions, permissionIds) =>
  hasAnyGrantedPermission(permissions, permissionIds, CASE_PLAN_DETAIL_ALL_PLANS_PERMISSIONS_SET);

export const hasCasePlanEditPermission = (permissions, permissionIds) =>
  hasAnyGrantedPermission(permissions, permissionIds, CASE_PLAN_EDIT_PERMISSIONS_SET);

/** Accepts the canonical create slug or the legacy one during the permission transition. */
export const hasCasePlanCreatePermission = (permissions, permissionIds) =>
  hasAnyGrantedPermission(permissions, permissionIds, CASE_PLAN_CREATE_PERMISSIONS_SET);

const isPlanTypeFeatureEnabled = (locationFeatures, visibilityPlanType) =>
  locationFeatures?.some(
    (feature) =>
      feature?.status === true &&
      toVisibilityPlanType(feature?.featureName) === visibilityPlanType,
  ) === true;

export const resolveEntitledPlanTypesFromFeatures = (locationFeatures) =>
  REENTRY_VISIBILITY_PLAN_TYPES.filter((planTypeKey) =>
    isPlanTypeFeatureEnabled(locationFeatures, planTypeKey),
  );

export const isReentryPlanFeatureEnabled = (locationFeatures) =>
  resolveEntitledPlanTypesFromFeatures(locationFeatures).includes(REENTRY_PLAN_TYPES.MVP.feature);

export const shouldShowCreatePlanButton = (entitledPlanTypes = []) =>
  (entitledPlanTypes.includes(REENTRY_PLAN_TYPES.PREVIEW.feature) && entitledPlanTypes.includes(REENTRY_PLAN_TYPES.MVP.feature)) || entitledPlanTypes.includes(REENTRY_PLAN_TYPES.MVP.feature);

export const getEffectiveActiveFeatures = (location, allLocations = []) => {
  if (Array.isArray(location?.activeFeatures) && location.activeFeatures.length > 0) {
    return location.activeFeatures;
  }

  if (location?.parentLocationId) {
    const parent = allLocations.find((item) => item.locationId === location.parentLocationId);
    return parent?.activeFeatures || [];
  }

  return [];
};

const resolveVisibilityPlanTypesFromActiveFeatures = (activeFeatures) => {
  const entitledPlanTypes = new Set();

  (activeFeatures || []).forEach((featureName) => {
    const visibilityKey = toVisibilityPlanType(featureName);
    if (visibilityKey) {
      entitledPlanTypes.add(visibilityKey);
    }
  });

  return REENTRY_VISIBILITY_PLAN_TYPES.filter((planTypeKey) => entitledPlanTypes.has(planTypeKey));
};

const filterLocationsByScope = (locations, scopeLocationIds) => {
  if (!scopeLocationIds) {
    return locations;
  }

  return locations.filter((location) => scopeLocationIds.has(location.locationId));
};

export const resolveEntitledPlanTypesFromLocations = (locations, scopeLocationIds) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }

  const scopedLocations = filterLocationsByScope(locations, scopeLocationIds);
  const entitledPlanTypes = new Set();

  scopedLocations.forEach((location) => {
    const activeFeatures = getEffectiveActiveFeatures(location, locations);
    resolveVisibilityPlanTypesFromActiveFeatures(activeFeatures).forEach((planTypeKey) => {
      entitledPlanTypes.add(planTypeKey);
    });
  });

  return REENTRY_VISIBILITY_PLAN_TYPES.filter((planTypeKey) => entitledPlanTypes.has(planTypeKey));
};

const entitledPlanTypesFromLocations = (locations) =>
  resolveEntitledPlanTypesFromLocations(locations ?? []);

/** Same LFA source as the side-nav Reentry Planning item: `LOCATIONS.activeFeatures`. */
export const hasPreviewPlanTypeInLocations = (locations) =>
  entitledPlanTypesFromLocations(locations).includes(REENTRY_PLAN_TYPES.PREVIEW.feature);

/** Same LFA source as the side-nav Reentry Planning item: `LOCATIONS.activeFeatures`. */
export const hasMvpPlanTypeInLocations = (locations) =>
  entitledPlanTypesFromLocations(locations).includes(REENTRY_PLAN_TYPES.MVP.feature);

export const hasReentryPlanTypeInLocations = (locations) =>
  entitledPlanTypesFromLocations(locations).length > 0;

/**
 * All Plans View Plan is LFA-gated from the same `LOCATIONS` source as the side-nav:
 * preview LFA + preview-release permission, or reentry LFA + `case_plan_reentry_view_any_detail`.
 */
export const canShowAllPlansViewPlan = ({
  locations = [],
  hasPreviewReleasePermission = false,
  hasReentryPermission = false,
} = {}) => {
  const previewPath = hasPreviewPlanTypeInLocations(locations) && Boolean(hasPreviewReleasePermission);
  const reentryPath = hasMvpPlanTypeInLocations(locations) && Boolean(hasReentryPermission);

  return previewPath || reentryPath;
};

/**
 * Builds a scope Set that matches a location's `locationId` regardless of whether the
 * source ids are numbers or strings (autocomplete options and API locations disagree).
 */
const buildLocationIdScopeSet = (locationIds) => {
  const scope = new Set();
  (locationIds ?? []).forEach((id) => {
    if (id === null || id === undefined || id === '') {
      return;
    }
    scope.add(id);
    scope.add(String(id));
    const numeric = Number(id);
    if (!Number.isNaN(numeric)) {
      scope.add(numeric);
    }
  });
  return scope;
};

/**
 * True when the `reentry_plan` (MVP) LFA is enabled at any of the given location ids.
 * Reuses {@link resolveEntitledPlanTypesFromLocations} against the global LOCATIONS list
 * (same source the side-menu uses) rather than re-reading feature flags.
 */
export const hasReentryPlanLfaForLocationIds = (locations, locationIds) => {
  const scope = buildLocationIdScopeSet(locationIds);
  if (scope.size === 0) {
    return false;
  }
  return resolveEntitledPlanTypesFromLocations(locations ?? [], scope).includes(
    REENTRY_PLAN_TYPES.MVP.feature,
  );
};

/**
 * My Plans tab: assigned-detail must be granted on the catalog (not CBAC `isPermitted`,
 * which is true for every slug when CBAC is off). Preview-release never unlocks this tab.
 * Reentry MVP LFA is also required so preview-only locations stay on All Plans.
 *
 * @param {object} params
 * @param {Array} [params.locations]
 * @param {boolean} [params.hasAssignedDetailPermission]
 * @returns {boolean}
 */
export const canShowMyPlansTab = ({
  locations = [],
  hasAssignedDetailPermission = false,
} = {}) => Boolean(hasAssignedDetailPermission) && hasMvpPlanTypeInLocations(locations);

/**
 * Read access to the plans list (All Plans tab, module shell, side-nav), LFA-paired:
 * preview LFA + `case_plan_preview_release_access`, or reentry LFA + assigned-detail.
 *
 * Preview-release is deliberately tied to the preview LFA. It is not a user-configurable
 * permission, so on an MVP-only location it would otherwise grant listing that the staff
 * permission toggles cannot revoke.
 *
 * @param {object} params
 * @param {Array} [params.locations] - `LOCATIONS` entries carrying `activeFeatures`.
 * @param {Array<{permissionName: string, permissionId: number}>} params.permissions
 * @param {number[]} params.permissionIds
 * @returns {boolean} True when the staff member may list reentry plans.
 */
export const canListReentryPlans = ({ locations = [], permissions, permissionIds } = {}) => {
  const previewPath =
    hasPreviewPlanTypeInLocations(locations) &&
    hasCasePlanPreviewReleaseAccess(permissions, permissionIds);
  const reentryPath =
    hasReentryPlanTypeInLocations(locations) &&
    hasCasePlanAssignedDetailAccess(permissions, permissionIds);

  return previewPath || Boolean(reentryPath);
};

/**
 * Module-shell access gate: LFA-paired read access, except for superadmins, who keep
 * access on any case-plan read slug without LFA entitlement.
 *
 * Side-nav intentionally does not use this helper — it has no superadmin bypass
 * (product decision).
 */
export const canAccessReentryPlanning = (
  permissions,
  permissionIds,
  { isSuperAdmin = false, locations = [] } = {},
) => {
  if (isSuperAdmin) {
    return Boolean(hasCasePlanAccess(permissions, permissionIds));
  }

  return canListReentryPlans({ locations, permissions, permissionIds });
};

export const resolvePlanTypesFromLocations = (locations, scopeLocationIds) =>
  mapEntitledPlanTypesToApi(resolveEntitledPlanTypesFromLocations(locations, scopeLocationIds));

export const createViewPlanClickHandler = (onViewPlan, plan) => () => {
  onViewPlan(plan);
};

export const REENTRY_PLANS_LOCATION_COLUMN = 'location';

const FACILITY_LOCATION_TYPE_ID = 3;
const HOUSING_UNIT_LOCATION_TYPE_ID = 15;

const compareByLocationName = (a, b) =>
  String(a.locationName).localeCompare(String(b.locationName));

const pickFacilitiesSorted = (locations) =>
  locations
    .filter((item) => item.locationTypeId === FACILITY_LOCATION_TYPE_ID)
    .sort(compareByLocationName);

const pickHousingUnitsForFacility = (locations, facility) =>
  locations
    .filter(
      (item) =>
        item.parentLocationId === facility.locationId &&
        item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID,
    )
    .sort(compareByLocationName);

const buildFacilityWithHousingUnits = (locations, facility) => [
  facility,
  ...pickHousingUnitsForFacility(locations, facility),
];

const toNonEmptyLocationName = (item) => {
  const name = item?.locationName;
  return name != null && String(name).trim() !== '' ? String(name) : null;
};

export const getLocationFilterOptions = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  return pickFacilitiesSorted(locations)
    .flatMap((facility) => buildFacilityWithHousingUnits(locations, facility))
    .map(toNonEmptyLocationName)
    .filter(Boolean);
};

export const mapLocationFilterToLocationIds = (filters, locations = []) => {
  const filter = Array.isArray(filters)
    ? filters.find((item) => item.columnName === REENTRY_PLANS_LOCATION_COLUMN)
    : null;

  if (!filter?.value) {
    return undefined;
  }

  const locationNames = String(filter.value)
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

  if (locationNames.length === 0) {
    return undefined;
  }

  const locationIds = locations
    .filter(
      (item) =>
        locationNames.includes(item.locationName) &&
        (item.locationTypeId === FACILITY_LOCATION_TYPE_ID ||
          item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID),
    )
    .map((item) => item.locationId);

  return locationIds.length > 0 ? locationIds.join(',') : undefined;
};
